import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { COLLECTION_PAYMENT_FREQUENCIES, type CollectionRuleField } from "@shared/collections-settings";
import { RULE_FIELD_LABELS, type CollectionPropertyRow } from "./_shared";

// Per-property rule overrides (Task #4830). Every field is either INHERITED
// from the account defaults or OVERRIDDEN here; clearing an override writes
// null, which is what the resolver reads as "inherit". The badge for each field
// comes from `resolved.sources`, computed by the same shared helper the engine
// uses — so what this sheet shows is what a run will actually apply.

const NUMERIC_FIELDS: CollectionRuleField[] = [
  "negativeBalanceThreshold",
  "paymentPercentage",
  "paymentDeadlineDays",
  "offerLimit",
  "cooldownDays",
  "offerResetDays",
];

type FormState = Record<string, string>;

function overridesToForm(row: CollectionPropertyRow): FormState {
  const o = row.overrides;
  return {
    negativeBalanceThreshold: o?.negativeBalanceThreshold ?? "",
    paymentPercentage: o?.paymentPercentage ?? "",
    paymentFrequency: o?.paymentFrequency ?? "",
    paymentDeadlineDays: o?.paymentDeadlineDays != null ? String(o.paymentDeadlineDays) : "",
    offerLimit: o?.offerLimit != null ? String(o.offerLimit) : "",
    offerResetDays: o?.offerResetDays != null ? String(o.offerResetDays) : "",
    cooldownDays: o?.cooldownDays != null ? String(o.cooldownDays) : "",
  };
}

export function OverrideEditor({
  property,
  open,
  onOpenChange,
  readOnly,
}: {
  property: CollectionPropertyRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  readOnly: boolean;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [form, setForm] = useState<FormState>({});

  useEffect(() => {
    if (property) setForm(overridesToForm(property));
  }, [property]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!property) return;
      // An empty input means "inherit", which the API expects as an explicit
      // null (undefined would leave the existing override in place).
      const overrides: Record<string, string | number | null> = {};
      for (const field of NUMERIC_FIELDS) {
        const raw = (form[field] ?? "").trim();
        if (raw === "") { overrides[field] = null; continue; }
        const n = Number(raw);
        if (!Number.isFinite(n)) throw new Error(`${RULE_FIELD_LABELS[field]} must be a number.`);
        overrides[field] = field === "negativeBalanceThreshold" || field === "paymentPercentage" ? String(n) : Math.trunc(n);
      }
      overrides.paymentFrequency = (form.paymentFrequency ?? "") === "" ? null : form.paymentFrequency;

      const res = await apiRequest("PUT", `/api/app/collections/properties/${property.propertyId}`, { overrides });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/collections/properties"] });
      toast({ title: "Rules saved", description: "Per-property collection rules have been updated." });
      onOpenChange(false);
    },
    onError: (e: Error) => toast({
      title: "Couldn't save rules",
      description: parseErrorMessage(e, "Couldn't save the property rules. Please try again."),
      variant: "destructive",
    }),
  });

  if (!property) return null;

  const sources = property.resolved.sources;

  const renderBadge = (field: CollectionRuleField) => (
    sources[field] === "override"
      ? <Badge variant="secondary" data-testid={`badge-source-${field}`}>Custom</Badge>
      : <Badge variant="outline" data-testid={`badge-source-${field}`}>Inherited</Badge>
  );

  return (
    <Sheet
      open={open}
      // Dismissal is gated on the mutation so a stray click can't drop an
      // in-flight save (enforced project-wide by a custom lint rule).
      onOpenChange={(next) => { if (!saveMutation.isPending) onOpenChange(next); }}
    >
      <SheetContent
        className="w-full overflow-y-auto sm:max-w-lg"
        onInteractOutside={(e) => { if (saveMutation.isPending) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (saveMutation.isPending) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (saveMutation.isPending) e.preventDefault(); }}
        data-testid="sheet-override-editor"
      >
        <SheetHeader>
          <SheetTitle>Collection rules — {property.name}</SheetTitle>
          <SheetDescription>
            Leave a field blank to inherit the account default. Anything you set here applies only to this property.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-5">
          {NUMERIC_FIELDS.map((field) => (
            <div key={field}>
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor={`override-${field}`}>{RULE_FIELD_LABELS[field]}</Label>
                {renderBadge(field)}
              </div>
              <Input
                id={`override-${field}`}
                type="number"
                value={form[field] ?? ""}
                disabled={readOnly}
                placeholder={`Inherited: ${String(property.resolved[field] ?? "all-time")}`}
                onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                className="mt-1 w-48"
                data-testid={`input-override-${field}`}
              />
            </div>
          ))}

          <div>
            <div className="flex items-center justify-between gap-2">
              <Label>{RULE_FIELD_LABELS.paymentFrequency}</Label>
              {renderBadge("paymentFrequency")}
            </div>
            <Select
              value={form.paymentFrequency === "" ? "__inherit__" : (form.paymentFrequency ?? "__inherit__")}
              disabled={readOnly}
              onValueChange={(v) => setForm({ ...form, paymentFrequency: v === "__inherit__" ? "" : v })}
            >
              <SelectTrigger className="mt-1 w-48" data-testid="select-override-payment-frequency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__inherit__">
                  Inherited ({property.resolved.paymentFrequency})
                </SelectItem>
                {COLLECTION_PAYMENT_FREQUENCIES.map((f) => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <SheetFooter className="mt-8 gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saveMutation.isPending}
            data-testid="button-override-cancel"
          >
            Cancel
          </Button>
          <Button
            className="bg-brand hover:bg-brand-hover text-white"
            onClick={() => saveMutation.mutate()}
            disabled={readOnly || saveMutation.isPending}
            data-testid="button-override-save"
          >
            {saveMutation.isPending ? "Saving..." : "Save rules"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
